import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Modal, TextField } from "@mui/material";
import Button from "../../../Subcomponents/Button";
import Close from "../../Icons/Close";
import { errorTost, successTost } from "../../../Services/Toast";
import {
  getCrm2Bookings, getAppointment, getAvailability, bookAppointment, rescheduleAppointment,
  sendBookingReminder, appointmentError,
} from "../../../Services/Crm2Appointment";
import cssClasses from "./Crm2Appointment.module.css";

// CRM 2.0 registration appointment (M13) on today's Agreement card.
//   <Crm2AppointmentPanel allotmentPaymentId />  shows only for CRM 2.0 bookings
//   <OldFlowOnly allotmentPaymentId>…</OldFlowOnly> hides today's Anulom Book Slot for them
// Old-flow cards render exactly as before (OldFlowOnly shows its children until
// the server says the booking is CRM 2.0).

const HALF = { 1: "1st half", 2: "2nd half" };
const WHO = { CRM: "Only CRM", CUSTOMER: "Only customer", BOTH: "CRM and customer" };
const WEEKDAY = ["", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const btnPrimary = { width: "fit-content", color: "#FFFFFF", backgroundColor: "#006AFF", border: "1px solid #006AFF", fontSize: "1.4rem", lineHeight: "2rem", whiteSpace: "nowrap" };
const btnSecondary = { width: "fit-content", color: "#344054", backgroundColor: "#FFFFFF", fontSize: "1.4rem", lineHeight: "2rem", whiteSpace: "nowrap" };
const niceDate = (key) => (key ? new Date(`${key}T00:00:00Z`).toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" }) : "");

// ---- which bookings on screen are CRM 2.0: one batched call, cached ------------------
// Only answers from the server are cached. A failed call is not: the card shows as
// today (old flow) and the hook asks again shortly, so one network blip does not
// hide the CRM 2.0 panel until a full reload.
const known = new Map(); // apId → true / false
const waiting = new Map(); // apId → [resolve]
const RETRY_MS = 5000;
const MAX_RETRIES = 3;
let timer = null;
function flush() {
  timer = null;
  const ids = [...waiting.keys()];
  const resolvers = new Map(waiting);
  waiting.clear();
  getCrm2Bookings(ids)
    .then(({ crm2 }) => ids.forEach((id) => known.set(id, crm2.includes(id))))
    .catch(() => {}) // not cached; resolves as "unknown" (null) below
    .finally(() => resolvers.forEach((list, id) => list.forEach((r) => r(known.has(id) ? known.get(id) : null))));
}
function lookup(apId) {
  if (known.has(apId)) return Promise.resolve(known.get(apId));
  return new Promise((resolve) => {
    waiting.set(apId, [...(waiting.get(apId) || []), resolve]);
    if (!timer) timer = setTimeout(flush, 50);
  });
}
function useIsCrm2(apId) {
  const [value, setValue] = useState(apId && known.has(apId) ? known.get(apId) : null);
  useEffect(() => {
    let alive = true;
    let retryTimer = null;
    const ask = (tries) =>
      lookup(apId).then((v) => {
        if (!alive) return;
        setValue(v);
        if (v === null && tries < MAX_RETRIES) retryTimer = setTimeout(() => ask(tries + 1), RETRY_MS);
      });
    if (apId) ask(0);
    return () => {
      alive = false;
      clearTimeout(retryTimer);
    };
  }, [apId]);
  return value;
}

export function OldFlowOnly({ allotmentPaymentId, children }) {
  return useIsCrm2(allotmentPaymentId) === true ? null : children;
}

function useCanBook() {
  const user = useSelector((state) => state.Auth.user);
  return user?.roleId?.level == 1 || !!user?.roleId?.accessibleTabs?.["All Allotments"]?.["CRM 2.0 Appointment"]?.["Book and reschedule"]?.isAccessible;
}

// ---- the card panel ------------------------------------------------------------------------
export function Crm2AppointmentPanel({ allotmentPaymentId }) {
  const isCrm2 = useIsCrm2(allotmentPaymentId);
  const canBook = useCanBook();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [modal, setModal] = useState(null); // "book" | "reschedule"
  const [sending, setSending] = useState(false);

  const load = () =>
    getAppointment(allotmentPaymentId)
      .then((d) => {
        setInfo(d);
        setError("");
      })
      .catch((err) => setError(appointmentError(err)));
  useEffect(() => {
    if (isCrm2) load();
  }, [isCrm2, allotmentPaymentId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isCrm2 !== true) return null;
  if (error) return <div className={cssClasses.panel}><p className={cssClasses.error}>{error}</p></div>;
  if (!info) return <div className={cssClasses.panel}><p className={cssClasses.hint}>Loading appointment…</p></div>;

  const appt = info.appointment;
  const booked = info.appointmentBooked;
  let chip;
  if (booked) chip = <span className={`${cssClasses.chip} ${cssClasses.chipOk}`}>Slot booked</span>;
  else if (appt?.status === "NOT_DONE") chip = <span className={`${cssClasses.chip} ${cssClasses.chipErr}`}>Not registered · book again</span>;
  else if (!info.open) chip = <span className={cssClasses.chip}>Appointment not open yet</span>;
  else chip = <span className={`${cssClasses.chip} ${info.whoBooks === "CRM" ? cssClasses.chipInd : cssClasses.chipWarn}`}>{info.whoBooks === "CRM" ? "Book slot" : "Customer to book slot"}</span>;

  async function remind() {
    setSending(true);
    try {
      await sendBookingReminder(allotmentPaymentId);
      successTost("Reminder sent to the customer by email");
    } catch (err) {
      errorTost(appointmentError(err));
    } finally {
      setSending(false);
    }
  }

  return (
    <div className={cssClasses.panel}>
      <div className={cssClasses.panelHead}>
        <span className={cssClasses.title}>Registration appointment</span>
        {chip}
      </div>
      <p className={cssClasses.hint}>Who books (project setting): {WHO[info.whoBooks] || info.whoBooks}</p>
      {!info.open && !booked && <p className={cssClasses.hint}>{info.notOpenReason}</p>}
      {appt && (
        <div className={cssClasses.apptBox}>
          <b>{niceDate(appt.date)} · {HALF[appt.half]}</b>
          <span>{appt.office?.officeName}{appt.office?.area ? ` · ${appt.office.area}` : ""}</span>
          {appt.office?.mapUrl && <a href={appt.office.mapUrl} target="_blank" rel="noreferrer">Open in Google Maps</a>}
          <span className={cssClasses.hint}>Booked by {appt.bookedByType === "CUSTOMER" ? "the customer" : "the CRM"}</span>
          {appt.rebookedFrom && (
            <span className={`${cssClasses.chip} ${cssClasses.chipWarn}`}>
              Rebooked · was {niceDate(appt.rebookedFrom.date)}{appt.rebookedFrom.reason ? ` (${appt.rebookedFrom.reason})` : ""}
            </span>
          )}
        </div>
      )}
      {canBook && info.open && (
        <div className={cssClasses.actions}>
          {!booked && info.customerMayBook && <Button style={btnSecondary} btnName="Send reminder" disable={sending} isLoading={sending} clicked={remind} />}
          {!booked && info.staffMayBook && <Button style={btnPrimary} btnName="Book Slot" clicked={() => setModal("book")} />}
          {booked && info.staffMayBook && <Button style={btnSecondary} btnName="Reschedule" clicked={() => setModal("reschedule")} />}
        </div>
      )}
      {modal && (
        <BookAppointmentModal info={info} reschedule={modal === "reschedule"} onClose={() => setModal(null)}
          onDone={(next) => {
            setModal(null);
            setInfo(next);
          }} />
      )}
    </div>
  );
}

// ---- Book Registration Appointment / Reschedule pop-up -------------------------------------
function BookAppointmentModal({ info, reschedule, onClose, onDone }) {
  const [officeId, setOfficeId] = useState(reschedule && info.appointment?.office?.officeId ? info.appointment.office.officeId : info.offices[0]?.officeId);
  const [page, setPage] = useState(null);
  const [from, setFrom] = useState(null);
  const [pick, setPick] = useState(null); // { date, half }
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!officeId) return;
    setLoading(true);
    setPick(null);
    getAvailability(info.allotmentPaymentId, officeId, from)
      .then(setPage)
      .catch((err) => errorTost(appointmentError(err)))
      .finally(() => setLoading(false));
  }, [officeId, from, info.allotmentPaymentId]);

  async function save() {
    setSaving(true);
    try {
      const res = reschedule
        ? await rescheduleAppointment(info.allotmentPaymentId, info.appointment.slotBookingId, officeId, pick.date, pick.half, reason)
        : await bookAppointment(info.allotmentPaymentId, officeId, pick.date, pick.half);
      const office = info.offices.find((o) => o.officeId === officeId);
      successTost(`${reschedule ? "Rescheduled" : "Appointment booked"}: ${office?.officeName}, ${niceDate(pick.date)}, ${HALF[pick.half]}.${res.emailed ? " Customer told by email." : " Customer message not sent: it is Off in Settings › CRM 2.0 › Notifications, or no email on record."}`);
      onDone(res.appointment);
    } catch (err) {
      errorTost(appointmentError(err));
      // Capacity may have changed (someone took the last place): reload the page of dates.
      getAvailability(info.allotmentPaymentId, officeId, from).then(setPage).catch(() => {});
    } finally {
      setSaving(false);
    }
  }

  const ready = !!pick && (!reschedule || reason.trim().length >= 3);
  const halfHours = (h) => (h === 1 ? page?.office?.firstHalfHours : page?.office?.secondHalfHours);

  return (
    <Modal open onClose={(_, r) => r !== "backdropClick" && onClose()} sx={{ ".MuiBackdrop-root": { background: "rgba(52, 64, 84, 0.7)" } }} disableAutoFocus>
      <Box className={cssClasses.modal}>
        <div className={cssClasses.modalHeader}>
          <span>{reschedule ? "Reschedule Appointment" : "Book Registration Appointment"}</span>
          <span className={cssClasses.modalClose} onClick={onClose} role="button" tabIndex={0} aria-label="Close"><Close width="1.2rem" height="1.2rem" /></span>
        </div>
        <div className={cssClasses.modalContent}>
          <p className={cssClasses.hint}>
            {[info.projectName, info.unitNo].filter(Boolean).join(" · ")}
            {info.attendees?.length ? ` · ${info.attendees.join(", ")}` : ""} · all applicants attend
          </p>
          {reschedule && info.appointment && (
            <div className={cssClasses.nowBooked}>
              Now booked: {info.appointment.office?.officeName}, {niceDate(info.appointment.date)}, {HALF[info.appointment.half]}
            </div>
          )}

          <span className={cssClasses.label}>Sub-Registrar office</span>
          {info.offices.length > 1 && <p className={cssClasses.hint}>This project uses two offices; choose one.</p>}
          <div className={cssClasses.officeCards}>
            {info.offices.map((o) => (
              <button key={o.officeId} type="button" className={`${cssClasses.officeCard} ${o.officeId === officeId ? cssClasses.officeCardOn : ""}`}
                onClick={() => { setOfficeId(o.officeId); setFrom(null); }}>
                <b>{o.officeName}</b>
                <span>{o.area}</span>
                <span className={cssClasses.hint}>{o.address}</span>
              </button>
            ))}
          </div>

          <span className={cssClasses.label}>Date and half of the day</span>
          <p className={cssClasses.hint}>Earliest date {niceDate(page?.minDate || info.minDate)} (setting).</p>
          <div className={cssClasses.legend}>
            <span><i className={cssClasses.dotAvail} />Available</span>
            <span><i className={cssClasses.dotFew} />Few places</span>
            <span><i className={cssClasses.dotFull} />Fully booked</span>
            <span><i className={cssClasses.dotClosed} />Closed / holiday</span>
          </div>
          {loading ? (
            <p className={cssClasses.hint}>Loading dates…</p>
          ) : (
            <div className={cssClasses.dayGrid}>
              {(page?.days || []).map((d) => (
                <div key={d.date} className={`${cssClasses.day} ${d.closedReason ? cssClasses.dayClosed : ""}`}>
                  <span className={cssClasses.dayName}>{WEEKDAY[d.weekday]} {d.date.slice(8, 10)}/{d.date.slice(5, 7)}</span>
                  {d.closedReason ? (
                    <span className={cssClasses.closedText}>Closed · {d.closedReason}</span>
                  ) : (
                    d.halves.map((h) => {
                      const on = pick?.date === d.date && pick?.half === h.half;
                      const disabled = h.status === "CLOSED" || h.status === "FULL";
                      return (
                        <button key={h.half} type="button" disabled={disabled}
                          className={`${cssClasses.halfBtn} ${cssClasses[`half${h.status}`]} ${on ? cssClasses.halfOn : ""}`}
                          onClick={() => setPick({ date: d.date, half: h.half })}>
                          <span>{HALF[h.half]}{halfHours(h.half) ? ` · ${halfHours(h.half)}` : ""}</span>
                          <span>{h.status === "CLOSED" ? "Closed" : h.status === "FULL" ? "Full" : `${h.left} left`}</span>
                        </button>
                      );
                    })
                  )}
                </div>
              ))}
            </div>
          )}
          <div className={cssClasses.pager}>
            {from && <Button style={btnSecondary} btnName="‹ Earlier dates" clicked={() => setFrom(null)} />}
            {page?.nextFrom && <Button style={btnSecondary} btnName="Later dates ›" clicked={() => setFrom(page.nextFrom)} />}
          </div>

          {reschedule && (
            <>
              <span className={cssClasses.label}>Reason for the new date <span className={cssClasses.required}>*</span></span>
              <TextField size="small" multiline minRows={2} value={reason} onChange={(e) => setReason(e.target.value)} inputProps={{ maxLength: 300, style: { fontSize: "1.4rem" } }} />
            </>
          )}
          {pick && (
            <p className={cssClasses.summary}>
              Your choice: {info.offices.find((o) => o.officeId === officeId)?.officeName}, {niceDate(pick.date)}, {HALF[pick.half]}
            </p>
          )}
        </div>
        <div className={cssClasses.modalFooter}>
          <Button style={btnSecondary} btnName="Cancel" clicked={onClose} />
          <Button style={btnPrimary} btnName={reschedule ? "Reschedule" : "Book Slot"} disable={!ready || saving} isLoading={saving} clicked={save} />
        </div>
      </Box>
    </Modal>
  );
}
